import React, { Component } from "react";
import {
  withModulesManager,
  formatMessage,
  formatDateFromISO,
} from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@material-ui/core";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
  LocalHospital as FacilityIcon,
} from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchHealthFacilityEnrollment,
  updateHealthFacilityEnrollment,
  submitHealthFacilityEnrollment,
} from "../../actions";
import {
  toBackendFacilityPayload,
  normalizeFacilityData,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: {
    margin: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    flex: 1,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  avatar: {
    backgroundColor: theme.palette.secondary.main,
    width: 80,
    height: 80,
    fontSize: "2rem",
    margin: "auto",
    marginBottom: theme.spacing(2),
  },
  formCard: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    marginRight: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  cancelButton: {
    backgroundColor: theme.palette.grey[500],
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.grey[700],
    },
  },
  serviceCheckbox: {
    marginBottom: theme.spacing(1),
  },
});

class HealthFacilityEnrollmentEdit extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: {
        facilityName: "",
        facilityCode: "",
        facilityType: "Hospital",
        location: "",
        address: "",
        phone: "",
        email: "",
        website: "",
        status: "Active",
        capacity: "",
        currentOccupancy: "",
        specializations: [],
        license: "",
        accreditation: "",
        operatingHours: "",
        emergencyServices: false,
        ambulanceServices: false,
        laboratoryServices: false,
        radiologyServices: false,
        pharmacyServices: false,
        director: "",
        contactPerson: "",
        establishedYear: "",
        numberOfStaff: "",
        numberOfDoctors: "",
        numberOfNurses: "",
        annualPatients: "",
        notes: "",
      },
      loading: false,
      errors: {},
    };
  }

  componentDidMount() {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid && uuid !== "new" && this.props.fetchHealthFacilityEnrollment) {
      this.props.fetchHealthFacilityEnrollment(this.props.modulesManager, uuid);
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.healthFacilityEnrollment &&
      this.props.healthFacilityEnrollment !== prevProps.healthFacilityEnrollment
    ) {
      const e = this.props.healthFacilityEnrollment;
      const d = normalizeFacilityData(e.facilityData);
      this.setState({
        formData: {
          ...this.state.formData,
          uuid: e.uuid,
          facilityName: d.name || "",
          facilityCode: d.code || "",
          facilityType: d.level || this.state.formData.facilityType,
          location: d.locationId || "",
          address: d.address || "",
          phone: d.phone || "",
          email: d.email || "",
          status: e.status || "DRAFT",
          notes: e.notes || "",
          legalFormId: d.legalFormId || "",
          careType: d.careType || "",
          level: d.level || "",
          locationId: d.locationId || "",
        },
      });
    }
  }

  handleBack = () => {
    const enrollmentId =
      this.state.formData?.uuid || this.props.match?.params?.enrollmentId;
    if (this.props.history && enrollmentId && enrollmentId !== "new") {
      this.props.history.push(`/enrollment/healthfacility/${enrollmentId}/detail`);
    } else if (this.props.history) {
      this.props.history.push("/enrollment/healthfacility");
    } else {
      window.history.back();
    }
  };

  handleCancel = () => {
    this.handleBack();
  };

  handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: value,
      },
    });
  };

  handleSpecializationChange = (specialization) => (event) => {
    const { formData } = this.state;
    let newSpecializations = [...formData.specializations];

    if (event.target.checked) {
      if (!newSpecializations.includes(specialization)) {
        newSpecializations.push(specialization);
      }
    } else {
      newSpecializations = newSpecializations.filter(
        (spec) => spec !== specialization
      );
    }

    this.setState({
      formData: {
        ...formData,
        specializations: newSpecializations,
      },
    });
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    if (!formData.facilityName.trim()) {
      errors.facilityName = "Facility name is required";
    }

    if (!formData.facilityCode.trim()) {
      errors.facilityCode = "Facility code is required";
    }

    if (!formData.location.trim()) {
      errors.location = "Location is required";
    }

    if (!formData.address.trim()) {
      errors.address = "Address is required";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSave = async () => {
    if (!this.validateForm()) {
      return;
    }

    const { formData } = this.state;
    const uuid =
      formData.uuid || this.props.healthFacilityEnrollment?.uuid;
    if (!uuid) return;

    this.setState({ loading: true });
    try {
      await Promise.resolve(
        this.props.updateHealthFacilityEnrollment(
          this.props.modulesManager,
          {
            uuid,
            facilityData: toBackendFacilityPayload(formData),
            notes: formData.notes,
          },
          "Update Health Facility Enrollment"
        )
      );
      this.handleBack();
    } catch (error) {
      console.error("Save error:", error);
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { classes, intl, match } = this.props;
    const { formData, loading, errors } = this.state;
    const isNew = match.params.enrollmentId === "new";

    const facilityTypes = [
      "Hospital",
      "Health Center",
      "Clinic",
      "Dispensary",
      "Specialized Center",
    ];
    const statusOptions = ["Active", "Inactive", "Pending"];
    const specializationOptions = [
      "Emergency",
      "Surgery",
      "Pediatrics",
      "Cardiology",
      "Neurology",
      "Orthopedics",
      "Gynecology",
      "Psychiatry",
      "Oncology",
      "Radiology",
    ];

    return (
      <Box className={classes.page}>
        <Card style={{ margin: 16 }}>
          <CardContent style={{ padding: 24 }}>
            <Box className={classes.header}>
              <Box display="flex" alignItems="center" flex={1}>
                <IconButton
                  className={classes.backButton}
                  onClick={this.handleBack}
                  color="primary"
                >
                  <BackIcon />
                </IconButton>
                <Typography variant="h4" className={classes.title}>
                  {isNew
                    ? formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.titleNew"
                      )
                    : formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.title"
                      )}
                </Typography>
              </Box>
              <Box className={classes.actions}>
                <Button
                  className={classes.saveButton}
                  onClick={this.handleSave}
                  disabled={loading}
                  startIcon={<SaveIcon />}
                >
                  {formatMessage(intl, "enrollment", "general.save")}
                </Button>
                <Button
                  className={classes.cancelButton}
                  onClick={this.handleCancel}
                  startIcon={<CancelIcon />}
                >
                  {formatMessage(intl, "enrollment", "general.cancel")}
                </Button>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12} md={6}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      mb={2}
                    >
                      <Avatar className={classes.avatar}>
                        <FacilityIcon fontSize="large" />
                      </Avatar>
                    </Box>

                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.basicInfo"
                      )}
                    </Typography>

                    <TextField
                      fullWidth
                      label="Facility Name"
                      value={formData.facilityName}
                      onChange={this.handleChange("facilityName")}
                      error={!!errors.facilityName}
                      helperText={errors.facilityName}
                      className={classes.formField}
                      required
                    />

                    <TextField
                      fullWidth
                      label="Facility Code"
                      value={formData.facilityCode}
                      onChange={this.handleChange("facilityCode")}
                      error={!!errors.facilityCode}
                      helperText={errors.facilityCode}
                      className={classes.formField}
                      required
                    />

                    <FormControl fullWidth className={classes.formField}>
                      <InputLabel>Facility Type</InputLabel>
                      <Select
                        value={formData.facilityType}
                        onChange={this.handleChange("facilityType")}
                      >
                        {facilityTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth className={classes.formField}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        onChange={this.handleChange("status")}
                      >
                        {statusOptions.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Location"
                      value={formData.location}
                      onChange={this.handleChange("location")}
                      error={!!errors.location}
                      helperText={errors.location}
                      className={classes.formField}
                      required
                    />

                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={this.handleChange("address")}
                      error={!!errors.address}
                      helperText={errors.address}
                      className={classes.formField}
                      multiline
                      rows={3}
                      required
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12} md={6}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.contactInfo"
                      )}
                    </Typography>

                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone}
                      onChange={this.handleChange("phone")}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      className={classes.formField}
                      required
                    />

                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={this.handleChange("email")}
                      error={!!errors.email}
                      helperText={errors.email}
                      className={classes.formField}
                      required
                    />

                    <TextField
                      fullWidth
                      label="Website"
                      value={formData.website}
                      onChange={this.handleChange("website")}
                      className={classes.formField}
                    />

                    <TextField
                      fullWidth
                      label="Director"
                      value={formData.director}
                      onChange={this.handleChange("director")}
                      className={classes.formField}
                    />

                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contactPerson}
                      onChange={this.handleChange("contactPerson")}
                      className={classes.formField}
                    />

                    <TextField
                      fullWidth
                      label="Operating Hours"
                      value={formData.operatingHours}
                      onChange={this.handleChange("operatingHours")}
                      className={classes.formField}
                      placeholder="e.g., 24/7 or 8:00 AM - 6:00 PM"
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Capacity and Staff */}
              <Grid item xs={12} md={6}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.capacityStaff"
                      )}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Total Capacity"
                          type="number"
                          value={formData.capacity}
                          onChange={this.handleChange("capacity")}
                          className={classes.formField}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Current Occupancy"
                          type="number"
                          value={formData.currentOccupancy}
                          onChange={this.handleChange("currentOccupancy")}
                          className={classes.formField}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Total Staff"
                          type="number"
                          value={formData.numberOfStaff}
                          onChange={this.handleChange("numberOfStaff")}
                          className={classes.formField}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Number of Doctors"
                          type="number"
                          value={formData.numberOfDoctors}
                          onChange={this.handleChange("numberOfDoctors")}
                          className={classes.formField}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Number of Nurses"
                          type="number"
                          value={formData.numberOfNurses}
                          onChange={this.handleChange("numberOfNurses")}
                          className={classes.formField}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Established Year"
                          type="number"
                          value={formData.establishedYear}
                          onChange={this.handleChange("establishedYear")}
                          className={classes.formField}
                        />
                      </Grid>
                    </Grid>

                    <TextField
                      fullWidth
                      label="Annual Patients"
                      type="number"
                      value={formData.annualPatients}
                      onChange={this.handleChange("annualPatients")}
                      className={classes.formField}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Licensing and Accreditation */}
              <Grid item xs={12} md={6}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.licensing"
                      )}
                    </Typography>

                    <TextField
                      fullWidth
                      label="License Number"
                      value={formData.license}
                      onChange={this.handleChange("license")}
                      className={classes.formField}
                    />

                    <TextField
                      fullWidth
                      label="Accreditation"
                      value={formData.accreditation}
                      onChange={this.handleChange("accreditation")}
                      className={classes.formField}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Services */}
              <Grid item xs={12} md={6}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.services"
                      )}
                    </Typography>

                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.emergencyServices}
                            onChange={this.handleChange("emergencyServices")}
                            color="primary"
                          />
                        }
                        label="Emergency Services"
                        className={classes.serviceCheckbox}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.ambulanceServices}
                            onChange={this.handleChange("ambulanceServices")}
                            color="primary"
                          />
                        }
                        label="Ambulance Services"
                        className={classes.serviceCheckbox}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.laboratoryServices}
                            onChange={this.handleChange("laboratoryServices")}
                            color="primary"
                          />
                        }
                        label="Laboratory Services"
                        className={classes.serviceCheckbox}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.radiologyServices}
                            onChange={this.handleChange("radiologyServices")}
                            color="primary"
                          />
                        }
                        label="Radiology Services"
                        className={classes.serviceCheckbox}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.pharmacyServices}
                            onChange={this.handleChange("pharmacyServices")}
                            color="primary"
                          />
                        }
                        label="Pharmacy Services"
                        className={classes.serviceCheckbox}
                      />
                    </FormGroup>
                  </CardContent>
                </Card>
              </Grid>

              {/* Specializations */}
              <Grid item xs={12} md={6}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(
                        intl,
                        "enrollment",
                        "healthFacilityEnrollment.edit.specializations"
                      )}
                    </Typography>

                    <FormGroup>
                      {specializationOptions.map((spec) => (
                        <FormControlLabel
                          key={spec}
                          control={
                            <Checkbox
                              checked={formData.specializations.includes(spec)}
                              onChange={this.handleSpecializationChange(spec)}
                              color="primary"
                            />
                          }
                          label={spec}
                          className={classes.serviceCheckbox}
                        />
                      ))}
                    </FormGroup>
                  </CardContent>
                </Card>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <Card className={classes.formCard}>
                  <CardContent>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      {formatMessage(intl, "enrollment", "general.notes")}
                    </Typography>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={formData.notes}
                      onChange={this.handleChange("notes")}
                      multiline
                      rows={4}
                      placeholder="Additional notes about the facility..."
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  healthFacilityEnrollment:
    state.enrollment?.healthFacilityEnrollmentDetail || null,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchHealthFacilityEnrollment,
      updateHealthFacilityEnrollment,
      submitHealthFacilityEnrollment,
    },
    dispatch
  );

export default withModulesManager(
  withStyles(styles)(
    injectIntl(
      connect(mapStateToProps, mapDispatchToProps)(HealthFacilityEnrollmentEdit)
    )
  )
);
